import { TEDDY_KEYS, type TeddyKey } from '../teddies.js'

/**
 * The vocabulary Rocky's Riddles is written in.
 *
 * Deliberately small and deliberately dull. The difficulty in this game is the
 * deduction, and a child who has to hold "the iridescent parasol" in her head
 * while working out who sat where is being asked two hard things at once.
 *
 * Each category carries its own verb, fixed. Varying it for style — "took" one
 * riddle and "picked" the next — would mean a clue's wording depended on
 * something other than the clue, which is how a generator and a reader come to
 * disagree about what a sentence says.
 */

export interface Thing {
  readonly key: string
  /** As a clue names it: "the pear", "the red hat". */
  readonly the: string
}

export interface RiddleCategory {
  readonly key: string
  /** Past tense, for a clue with a teddy as its subject: "Sky took the pear." */
  readonly past: string
  /** Bare, for what follows "did not": "Hoppity did not take the apple." */
  readonly bare: string
  /** How a holder is named: "the one **with** the pear", "the one **in** the red hat". */
  readonly holding: string
  /** What she is asked: "Who **took** the plum?" */
  readonly asked: string
  /**
   * Five, because five is the largest row a riddle uses. A category with fewer
   * things than there are teddies could not be dealt out one each, and the
   * whole game rests on the deal being a bijection.
   */
  readonly things: readonly Thing[]
}

const FRUIT: RiddleCategory = {
  key: 'fruit',
  past: 'took',
  bare: 'take',
  holding: 'with',
  asked: 'took',
  things: [
    { key: 'apple', the: 'the apple' },
    { key: 'pear', the: 'the pear' },
    { key: 'plum', the: 'the plum' },
    { key: 'banana', the: 'the banana' },
    { key: 'peach', the: 'the peach' },
  ],
}

const HAT: RiddleCategory = {
  key: 'hat',
  past: 'wore',
  bare: 'wear',
  holding: 'in',
  asked: 'wore',
  things: [
    { key: 'red', the: 'the red hat' },
    { key: 'blue', the: 'the blue hat' },
    { key: 'green', the: 'the green hat' },
    { key: 'yellow', the: 'the yellow hat' },
    { key: 'stripy', the: 'the stripy hat' },
  ],
}

/**
 * The categories, in the order a riddle takes them.
 *
 * Fruit first because a fruit is a thing you hold and a hat is a thing you
 * wear, and "who took what" is the easier question. The second one only
 * appears once she has reached the level where two of them cross.
 */
export const RIDDLE_CATEGORIES: readonly RiddleCategory[] = [FRUIT, HAT]

export function categoryByKey(key: string): RiddleCategory | undefined {
  return RIDDLE_CATEGORIES.find((category) => category.key === key)
}

/**
 * Who can be in a riddle.
 *
 * Everybody but Rocky. He is asking, and a detective who is also a suspect is
 * a joke she will enjoy in about six years.
 */
export const RIDDLE_CAST: readonly TeddyKey[] = TEDDY_KEYS.filter((key) => key !== 'rocky')
